import { useState, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [, navigate] = useLocation();
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      toast.error("Link inválido ou expirado");
      navigate("/login");
    } else {
      setToken(t);
    }
  }, []);

  const resetPassword = trpc.auth.resetPassword.useMutation({
    onSuccess: () => {
      setDone(true);
      setTimeout(() => navigate("/admin"), 2500);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    resetPassword.mutate({ token, newPassword });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #1a0a00 0%, #2d1200 40%, #1a0a00 100%)" }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #c97d2e, transparent)" }} />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #c97d2e, transparent)" }} />
      </div>

      <div className="w-full max-w-sm relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg" style={{ background: "linear-gradient(135deg, #c97d2e, #e8a84e)" }}>
            <span className="text-white font-bold text-2xl font-serif">B</span>
          </div>
          <h1 className="text-2xl font-bold text-white font-serif">Beleza & Bronze</h1>
          <p className="text-sm mt-1" style={{ color: "#c97d2e" }}>Painel Administrativo</p>
        </div>

        <Card className="border-0 shadow-2xl" style={{ background: "rgba(255,255,255,0.97)" }}>
          <CardHeader className="pb-2 pt-6 px-6">
            <h2 className="text-xl font-semibold text-gray-800 text-center">
              {done ? "Senha redefinida!" : "Criar nova senha"}
            </h2>
            <p className="text-sm text-gray-500 text-center mt-1">
              {done ? "Você será redirecionado ao painel em instantes." : "Digite sua nova senha abaixo."}
            </p>
          </CardHeader>
          <CardContent className="px-6 pb-6 pt-4">
            {done ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <CheckCircle2 className="w-14 h-14" style={{ color: "#c97d2e" }} />
                <p className="text-sm text-gray-600 text-center">Senha alterada com sucesso! Redirecionando...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="newPassword" className="text-gray-700 font-medium text-sm">Nova senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínimo 6 caracteres"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-9 pr-10 h-11 border-gray-200 focus:border-amber-500 focus:ring-amber-500/20"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-gray-700 font-medium text-sm">Confirmar senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Repita a senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-9 h-11 border-gray-200 focus:border-amber-500 focus:ring-amber-500/20"
                      required
                    />
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> As senhas não coincidem
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 font-semibold text-white shadow-md"
                  style={{ background: "linear-gradient(135deg, #c97d2e, #e8a84e)" }}
                  disabled={resetPassword.isPending || newPassword !== confirmPassword}
                >
                  {resetPassword.isPending ? "Salvando..." : "Salvar nova senha"}
                </Button>

                <Link href="/login">
                  <Button variant="ghost" className="w-full text-gray-500 hover:text-gray-700 text-sm">
                    Cancelar
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
